import api from '../../api/api';
import type { ProductCardProps } from '../../types/types';
import { getProductById } from '../../services/product/product';

// Centralized endpoint configuration
export const API_ENDPOINTS = {
  FAVORITES: '/favorite/',
  CART: '/cart/',
};

interface FavoriteItem {
  id: number;
  product: number;
}

interface CartItem {
  id: number;
  product: number;
  quantity: number;
  selectedFeature?: { id: number; attributes: any[] } | null;
}

const getAuthToken = (): string | null => {
  return localStorage.getItem("access");
};

// Retry logic for API calls
const retry = async <T>(fn: () => Promise<T>, retries: number = 3, delay: number = 1000): Promise<T> => {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn();
    } catch (e: any) {
      if (i === retries - 1) throw e;
      // console.error(`Retry ${i + 1}/${retries} failed:`, e);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  throw new Error("Max retries reached");
};

export const getFavorites = async (): Promise<FavoriteItem[]> => {
  const token = getAuthToken();
  // console.error("token:", token);
  if (!token) {
    console.warn("No token found. Skipping API request for favorites.");
    return [];
  }
  try {
    const response = await retry(() =>
      api.get(API_ENDPOINTS.FAVORITES, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      })
    );
    if (!Array.isArray(response.data)) {
      console.warn("Invalid favorites data from API:", response.data);
      return [];
    }
    // console.error("Fetched favorites from API:", response.data);
    return response.data;
  } catch (e: any) {
    console.error("Failed to fetch favorites from API:", {
      message: e.message,
      status: e.response?.status,
      url: e.config?.url,
      data: e.response?.data,
    });
    return [];
  }
};

export const addFavorite = async (productId: number): Promise<void> => {
  const token = getAuthToken();
  if (!token) throw new Error("User not authenticated");
  try {
    // console.error(`Sending POST to ${API_ENDPOINTS.FAVORITES} with productId:`, productId);
    const response = await retry(() =>
      api.post(
        API_ENDPOINTS.FAVORITES,
        { product: productId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      )
    );
    // console.error("Add favorite response:", response.data);
  } catch (e: any) {
    console.error("Failed to add favorite:", {
      productId,
      message: e.message,
      status: e.response?.status,
      url: e.config?.url,
      data: e.response?.data,
    });
    if (e.response?.status === 400) {
      console.warn("Bad request. Check payload format or product ID validity.");
    } else if (e.response?.status === 404) {
      console.warn("Favorites endpoint not found. Adding to local storage.");
      const localFavorites = JSON.parse(localStorage.getItem("liked") || "[]");
      if (!localFavorites.includes(productId)) {
        localFavorites.push(productId);
        localStorage.setItem("liked", JSON.stringify(localFavorites));
      }
    }
    throw e;
  }
};

export const removeFavorite = async (favoriteId: number): Promise<void> => {
  const token = getAuthToken();
  if (!token) throw new Error("User not authenticated");
  try {
    // console.error(`Sending DELETE to ${API_ENDPOINTS.FAVORITES}${favoriteId}/`);
    await retry(() =>
      api.delete(`${API_ENDPOINTS.FAVORITES}${favoriteId}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      })
    );
  } catch (e: any) {
    console.error("Failed to remove favorite:", {
      favoriteId,
      message: e.message,
      status: e.response?.status,
      url: e.config?.url,
      data: e.response?.data,
    });
    throw e;
  }
};

export const syncLocalFavorites = async (localFavorites: any[], syncedProductIds: number[]): Promise<void> => {
  const token = getAuthToken();
  if (!token) {
    console.warn("No token found. Skipping sync to API.");
    return;
  }
  for (const fav of localFavorites) {
    const productId = typeof fav === 'number' ? fav : fav.id;
    if (typeof productId !== 'number') {
      console.warn(`Invalid product ID for syncing: ${fav}`);
      continue;
    }
    // Skip if productId is already synced
    if (syncedProductIds.includes(productId)) {
      // console.error(`Skipping sync for productId ${productId} as it is already synced`);
      continue;
    }
    try {
      await addFavorite(productId);
    } catch (e) {
      console.error(`Failed to sync favorite ${productId}:`, e);
    }
  }
};

export const fetchFavoriteProducts = async (): Promise<ProductCardProps[]> => {
  const token = getAuthToken();
  if (!token) {
    console.warn("No token found. Returning empty favorites list.");
    return [];
  }
  try {
    const apiFavorites = await getFavorites();
    if (!apiFavorites.length) {
      // console.error("No favorites found in API response.");
    }
    const productPromises = apiFavorites.map(async (fav: FavoriteItem) => {
      if (typeof fav.product !== 'number') {
        console.warn(`Invalid product ID in API favorites: ${fav.product}`);
        return null;
      }
      try {
        const product = await getProductById(fav.product.toString());
        return {
          id: product.id,
          title: product.title,
          price: product.price,
          image: product.images,
          discount: product.discount || null,
          discount_price: product.discount_price || null,
          feature: product.feature,
          sold_count: product.sold_count ?? null,
          view_count: product.view_count ?? null,
          rating: product.rating ?? null,
        } as ProductCardProps;
      } catch (e) {
        console.error(`Failed to fetch product ${fav.product}:`, e);
        return null;
      }
    });
    const products = (await Promise.all(productPromises)).filter((p): p is ProductCardProps => p !== null);
    // console.error("Fetched favorites:", products);
    return products;
  } catch (e: any) {
    console.error("Failed to fetch favorite products from API:", {
      message: e.message,
      status: e.response?.status,
      url: e.config?.url,
      data: e.response?.data,
    });
    if (e.response?.status === 404) {
      console.warn("Favorites endpoint not found. Ensure backend endpoint is /api/v1/favorite/.");
    }
    return [];
  }
};

export const fetchLocalFavoriteProducts = async (): Promise<ProductCardProps[]> => {
  try {
    const likedItems = JSON.parse(localStorage.getItem("liked") || "[]");
    // console.error("Local liked items:", likedItems);
    const productIds: number[] = likedItems
      .map((item: any) => typeof item === 'number' ? item : item.id)
      .filter((id: any) => typeof id === 'number');

    if (productIds.length !== likedItems.length) {
      console.warn("Some invalid liked items in localStorage, filtering valid IDs");
    }

    const productPromises = productIds.map(async (productId: number) => {
      try {
        const product = await getProductById(productId.toString());
        return {
          id: product.id,
          title: product.title,
          price: product.price,
          image: product.images,
          discount: product.discount || null,
          discount_price: product.discount_price || null,
          feature: product.feature,
          sold_count: product.sold_count ?? null,
          view_count: product.view_count ?? null,
          rating: product.rating ?? null,
        } as ProductCardProps;
      } catch (e) {
        console.error(`Failed to fetch product ${productId}:`, e);
        return null;
      }
    });
    const products = (await Promise.all(productPromises)).filter((p): p is ProductCardProps => p !== null);
    // console.error("Local favorite products:", products);
    return products;
  } catch (e) {
    console.error("Failed to fetch local favorite products:", e);
    return [];
  }
};
